/**
 * Authentication Context
 * Manages user authentication state globally.
 *
 * Model: every visitor is silently signed in as an anonymous ("guest") user so
 * the whole app is usable with no account. When a guest chooses to save their
 * work, we *link* their anonymous account to Google in place — the uid never
 * changes, so all their existing data comes along automatically.
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import {
  User as FirebaseUser,
  signInAnonymously,
  signInWithPopup,
  linkWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '@/config/firebase'
import { analyticsEvents } from '@/utils/analytics'
import { User } from '@/types'
import toast from 'react-hot-toast'

interface AuthContextType {
  user: FirebaseUser | null
  userProfile: User | null
  loading: boolean
  error: Error | null
  /** True while a Google sign-in / upgrade popup is in flight */
  upgrading: boolean
  signInAsGuest: () => Promise<void>
  /** Upgrade the current guest to a permanent Google account (or sign into an existing one) */
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<FirebaseUser | null>(null)
  const [userProfile, setUserProfile] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [upgrading, setUpgrading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Create or update user profile in Firestore
  const createOrUpdateUserProfile = async (
    firebaseUser: FirebaseUser,
    method?: 'google' | 'anonymous'
  ) => {
    const userRef = doc(db, 'users', firebaseUser.uid)
    const userSnap = await getDoc(userRef)

    if (!userSnap.exists()) {
      // Create new user profile
      const newUserData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        isAnonymous: firebaseUser.isAnonymous,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        settings: {
          theme: 'light',
          emailNotifications: true,
          language: 'en',
        },
      }

      await setDoc(userRef, newUserData)
      setUserProfile(newUserData as User)

      if (method) {
        analyticsEvents.signupCompleted(method)
      }
    } else {
      // Update existing user profile
      const existingData = userSnap.data() as User

      await setDoc(
        userRef,
        {
          lastLoginAt: serverTimestamp(),
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          isAnonymous: firebaseUser.isAnonymous,
        },
        { merge: true }
      )

      setUserProfile({ ...existingData, lastLoginAt: serverTimestamp() as any })
    }
  }

  // Sign in anonymously (explicit — the app also does this automatically)
  const signInAsGuest = async () => {
    try {
      setError(null)
      const result = await signInAnonymously(auth)
      await createOrUpdateUserProfile(result.user, 'anonymous')
      toast.success('Signed in as guest!')
    } catch (err) {
      console.error('Error signing in anonymously:', err)
      setError(err as Error)
      toast.error('Failed to sign in as guest')
      throw err
    }
  }

  // Upgrade the guest account to Google (or sign into an existing Google account)
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider()
    const current = auth.currentUser
    setUpgrading(true)
    try {
      setError(null)

      if (current && current.isAnonymous) {
        // Link in place: keeps the same uid, so all guest data is preserved.
        const result = await linkWithPopup(current, provider)
        await createOrUpdateUserProfile(result.user, 'google')
        toast.success('Signed in — your lists are saved to your account!')
      } else {
        const result = await signInWithPopup(auth, provider)
        await createOrUpdateUserProfile(result.user, 'google')
        toast.success('Signed in with Google!')
      }
    } catch (err: any) {
      // The Google account already exists (e.g. created on another device).
      // Sign into it so the user gets their real account + data back.
      if (
        err?.code === 'auth/credential-already-in-use' ||
        err?.code === 'auth/email-already-in-use' ||
        err?.code === 'auth/account-exists-with-different-credential'
      ) {
        try {
          const result = await signInWithPopup(auth, provider)
          await createOrUpdateUserProfile(result.user, 'google')
          toast.success('Welcome back! Signed into your existing account.')
          // NOTE: anything created as a guest in THIS browser stays under the old
          // anonymous uid. Carrying it over requires a server-side merge (see the
          // scheduled/callable cleanup+merge function noted in the follow-ups).
        } catch (inner) {
          console.error('Error signing into existing account:', inner)
          setError(inner as Error)
          toast.error('Could not sign in. Please try again.')
          throw inner
        }
      } else if (
        err?.code === 'auth/popup-closed-by-user' ||
        err?.code === 'auth/cancelled-popup-request'
      ) {
        // User dismissed the popup — stay a guest, no error.
      } else {
        console.error('Error signing in with Google:', err)
        setError(err as Error)
        toast.error('Failed to sign in with Google')
        throw err
      }
    } finally {
      setUpgrading(false)
    }
  }

  // Sign out
  const signOut = async () => {
    try {
      setError(null)
      await firebaseSignOut(auth)
      setUser(null)
      setUserProfile(null)
      toast.success('Signed out successfully')
    } catch (err) {
      console.error('Error signing out:', err)
      setError(err as Error)
      toast.error('Failed to sign out')
      throw err
    }
  }

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      setUser(firebaseUser)

      if (firebaseUser) {
        // We have a user — let the app render immediately and sync the Firestore
        // profile in the background so first paint isn't blocked on a write.
        setLoading(false)
        createOrUpdateUserProfile(firebaseUser).catch((err: any) => {
          console.error('[AUTH] Error loading user profile:', err)

          if (
            err?.message?.includes('Failed to fetch') ||
            err?.message?.includes('Network request failed') ||
            err?.code === 'unavailable'
          ) {
            toast.error('Cannot connect to database. Please check browser extensions or ad blockers.')
          }

          setError(err as Error)

          // Keep the user usable with a minimal profile built from Auth data.
          setUserProfile({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            isAnonymous: firebaseUser.isAnonymous,
            settings: {
              theme: 'light',
              emailNotifications: true,
              language: 'en',
            },
          } as any as User)
        })
      } else {
        // No user yet — silently create an anonymous guest. This re-triggers the
        // listener with the guest user, which flips loading off above.
        try {
          await signInAnonymously(auth)
        } catch (err) {
          console.error('[AUTH] Error auto-signing in anonymously:', err)
          setUserProfile(null)
          setLoading(false)
        }
      }
    })

    return unsubscribe
  }, [])

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    error,
    upgrading,
    signInAsGuest,
    signInWithGoogle,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
